/*!
 * worldCoords.js — element geometry in the space window.playheadX lives in
 * © 2025 Rob Canning — GPLv3
 *
 * One rule, in one place, because getting it wrong is silent and has happened
 * three times:
 *
 *   element.getCTM()  maps into the nearest VIEWPORT, so it carries the
 *                     viewBox scale. window.playheadX does not have that
 *                     scale. Comparing the two is comparing different units.
 *
 * On a score that happens to draw at 1x the mistake is invisible. On
 * demo-transport, which draws at 2.342x, pin() stored 7777 for an element at
 * 3320 and waited for a playhead position past the end of a 6470-wide score,
 * so it never fired at all.
 *
 * Composing the two screen matrices cancels everything outside the SVG — the
 * viewBox, the page, any transform on an ancestor — and leaves only the
 * element's own chain inside the score, which is the space the playhead is
 * measured in.
 *
 * The distinguishing rule, for anyone reading a CTM elsewhere: composing two
 * CTMs is safe, using one alone is not. o2p and animShared compose theirs and
 * are correct; cuePin and speed used one each and were not.
 */

/**
 * The matrix from an element's own coordinates to its SVG's user space.
 * @returns {DOMMatrix|SVGMatrix|null}
 */
export function svgLocalMatrix(element) {
  const svg = element?.ownerSVGElement;
  if (svg?.getScreenCTM && element.getScreenCTM) {
    const svgM = svg.getScreenCTM();
    const elM = element.getScreenCTM();
    if (svgM && elM) {
      try { return svgM.inverse().multiply(elM); } catch { /* fall through */ }
    }
  }
  // Not rendered yet, or detached — getCTM is the best available answer and
  // is correct whenever there is no viewBox scale to strip.
  return element?.getCTM?.() ?? null;
}

/** World x of a point given in the element's own coordinates. */
export function worldX(element, localX, localY = 0) {
  const m = svgLocalMatrix(element);
  return m ? m.a * localX + m.c * localY + m.e : localX;
}

/** Both coordinates of a point given in the element's own coordinates. */
export function worldPoint(element, localX, localY = 0) {
  const m = svgLocalMatrix(element);
  if (!m) return { x: localX, y: localY };
  return {
    x: m.a * localX + m.c * localY + m.e,
    y: m.b * localX + m.d * localY + m.f,
  };
}

/**
 * svgLocalMatrix for a caller that has already inverted the root's screen CTM
 * once for the whole frame — a per-frame loop over many elements would
 * otherwise read and invert the same root matrix for each of them. Still a
 * composition of the two matrices, which is the rule above; only the
 * expensive half is hoisted.
 */
export function localMatrixFrom(rootInverse, element) {
  const elM = element?.getScreenCTM?.();
  if (rootInverse && elM) {
    try { return rootInverse.multiply(elM); } catch { /* fall through */ }
  }
  return svgLocalMatrix(element);
}

/**
 * World x of an element's centre, with the half-width and x-scale that go
 * with it — what a position trigger needs to know.
 */
export function worldCenterX(element) {
  const box = element.getBBox();
  const m = svgLocalMatrix(element);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  return {
    centerX: m ? m.a * cx + m.c * cy + m.e : cx,
    halfW: (m ? Math.abs(m.a) : 1) * box.width / 2,
    parentScaleX: m ? m.a : 1,
  };
}

/** World x of an element's left edge. */
export function worldLeftX(element) {
  const box = element.getBBox();
  return worldX(element, box.x, box.y);
}
